import { Outlet, useLocation, useNavigate, useSearchParams, NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import AdminNotificationCenter from '@/components/dashboard/AdminNotificationCenter';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [searchParams] = useSearchParams();

  // Compteurs pour les badges
  const [candidaturesCount, setCandidaturesCount] = useState(0);
  const [supportCount, setSupportCount] = useState(0);
  const [crmAlertesCount, setCrmAlertesCount] = useState(0);

  // Menu mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Debug visible si ?qa=1 ou dev
  const showDebug = searchParams.get('qa') === '1' || import.meta.env.DEV;

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleBackToSite = () => {
    navigate('/');
  };

  const isAdmin = profile?.role === 'ADMIN';

  // Charger les compteurs au montage et toutes les 30 secondes
  useEffect(() => {
    if (!isAdmin) return;

    const loadCounts = async () => {
      try {
        // Candidatures non traitées (NEW ou FORM_SUBMITTED)
        const { count: candidaturesNonTraitees } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .in('status', ['NEW', 'FORM_SUBMITTED']);

        setCandidaturesCount(candidaturesNonTraitees || 0);

        // Tickets ouverts (OPEN)
        const { count: ticketsOuverts } = await supabase
          .from('tickets')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'OPEN');

        setSupportCount(ticketsOuverts || 0);

        // Alertes CRM : dossiers sans activité depuis +7 jours
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const { data: societes } = await supabase
          .from('societes_recommandees')
          .select('updated_at, created_at, statut')
          .not('statut', 'in', '("valide","refuse")');

        const alertes = (societes || []).filter((s) => {
          const updatedAt = new Date(s.updated_at || s.created_at);
          return updatedAt < sevenDaysAgo;
        });

        setCrmAlertesCount(alertes.length);
      } catch (err) {
        console.warn('[AdminLayout] Erreur chargement compteurs:', err);
      }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 30000); // Refresh toutes les 30s

    return () => clearInterval(interval);
  }, [isAdmin]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#070707] flex flex-col">
      {/* Topbar admin compacte */}
      <div className="bg-[#0F1115] border-b border-[#232838]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-white whitespace-nowrap">Admin TP13</h1>
              
              {/* Bouton hamburger mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-[#141824] rounded-lg transition-colors cursor-pointer"
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>

              {/* Navigation desktop */}
              <nav className="hidden lg:flex items-center gap-1 flex-wrap">
                <NavLink
                  to="/admin"
                  end
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-dashboard-line mr-1.5"></i>
                  Vue d&apos;ensemble
                </NavLink>
                <NavLink
                  to="/admin/qa"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-user-search-line mr-1.5"></i>
                  Console QA
                </NavLink>
                <NavLink
                  to="/admin/utilisateurs"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-user-line mr-1.5"></i>
                  Utilisateurs
                </NavLink>
                <NavLink
                  to="/admin/apporteurs"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-user-star-line mr-1.5"></i>
                  Apporteurs
                </NavLink>
                <NavLink
                  to="/admin/collaborateurs"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-user-settings-line mr-1.5"></i>
                  Collaborateurs
                </NavLink>
                <NavLink
                  to="/admin/candidatures"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap relative ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-file-list-line mr-1.5"></i>
                  Candidatures
                  {candidaturesCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {candidaturesCount > 9 ? '9+' : candidaturesCount}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/societes"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-building-line mr-1.5"></i>
                  Sociétés
                </NavLink>
                <NavLink
                  to="/admin/crm"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap relative ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-line-chart-line mr-1.5"></i>
                  CRM
                  {crmAlertesCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {crmAlertesCount > 9 ? '9+' : crmAlertesCount}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/classement"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-trophy-line mr-1.5"></i>
                  Classement
                </NavLink>
                <NavLink
                  to="/admin/remuneration"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-money-euro-circle-line mr-1.5"></i>
                  Rémunération AO
                </NavLink>
                <NavLink
                  to="/admin/statistiques"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-bar-chart-line mr-1.5"></i>
                  Statistiques
                </NavLink>
                <NavLink
                  to="/admin/support"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap relative ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-customer-service-line mr-1.5"></i>
                  Support
                  {supportCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {supportCount > 9 ? '9+' : supportCount}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/documents"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-folder-line mr-1.5"></i>
                  Documents
                </NavLink>
                <NavLink
                  to="/admin/logs"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-[#DC143C] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                    }`
                  }
                >
                  <i className="ri-file-text-line mr-1.5"></i>
                  Logs
                </NavLink>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              {/* Centre de notifications admin */}
              <AdminNotificationCenter />
              
              <span className="text-sm text-gray-400 hidden sm:block">{user?.email || '—'}</span>
              <button
                onClick={handleBackToSite}
                className="px-3 py-2 bg-[#141824] hover:bg-[#232838] text-gray-300 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line mr-2"></i>
                <span className="hidden sm:inline">Retour</span>
              </button>
              <button
                onClick={handleLogout}
                className="px-3 py-2 bg-[#DC2626] hover:bg-[#DC2626]/80 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-2"></i>
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>

        {/* Menu mobile déroulant */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0F1115] border-b border-[#232838]">
            <nav className="px-4 py-3 space-y-1">
              <NavLink
                to="/admin"
                end
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-dashboard-line mr-2"></i>
                Vue d&apos;ensemble
              </NavLink>
              <NavLink
                to="/admin/qa"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-user-search-line mr-2"></i>
                Console QA
              </NavLink>
              <NavLink
                to="/admin/utilisateurs"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-user-line mr-2"></i>
                Utilisateurs
              </NavLink>
              <NavLink
                to="/admin/apporteurs"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-user-star-line mr-2"></i>
                Apporteurs
              </NavLink>
              <NavLink
                to="/admin/collaborateurs"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-user-settings-line mr-2"></i>
                Collaborateurs
              </NavLink>
              <NavLink
                to="/admin/candidatures"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-file-list-line mr-2"></i>
                Candidatures
                {candidaturesCount > 0 && (
                  <span className="ml-auto w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {candidaturesCount > 9 ? '9+' : candidaturesCount}
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/admin/societes"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-building-line mr-2"></i>
                Sociétés
              </NavLink>
              <NavLink
                to="/admin/crm"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-line-chart-line mr-2"></i>
                CRM
                {crmAlertesCount > 0 && (
                  <span className="ml-auto w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {crmAlertesCount > 9 ? '9+' : crmAlertesCount}
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/admin/classement"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-trophy-line mr-2"></i>
                Classement
              </NavLink>
              <NavLink
                to="/admin/remuneration"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-money-euro-circle-line mr-2"></i>
                Rémunération AO
              </NavLink>
              <NavLink
                to="/admin/statistiques"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-bar-chart-line mr-2"></i>
                Statistiques
              </NavLink>
              <NavLink
                to="/admin/support"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-customer-service-line mr-2"></i>
                Support
                {supportCount > 0 && (
                  <span className="ml-auto w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {supportCount > 9 ? '9+' : supportCount}
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/admin/documents"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-folder-line mr-2"></i>
                Documents
              </NavLink>
              <NavLink
                to="/admin/logs"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#141824]'
                  }`
                }
              >
                <i className="ri-file-text-line mr-2"></i>
                Logs
              </NavLink>
            </nav>
          </div>
        )}
      </div>

      {/* Zone outlet */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </div>

      {/* Debug panel visible si qa=1 ou dev */}
      {showDebug && (
        <div className="fixed bottom-4 right-4 z-50 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg text-xs font-mono max-w-xs shadow-lg">
          <p className="text-violet-400 font-bold mb-2">🔧 DEBUG - AdminLayout</p>
          <p className="text-violet-300">Email: {user?.email || '—'}</p>
          <p className="text-violet-300">Role: {profile?.role || '—'}</p>
          <p className="text-violet-300">isAdmin: {isAdmin ? 'true' : 'false'}</p>
          <p className="text-violet-300">Route: {location.pathname}</p>
          <p className="text-violet-300">Candidatures: {candidaturesCount}</p>
          <p className="text-violet-300">Support: {supportCount}</p>
          <p className="text-violet-300">CRM Alertes: {crmAlertesCount}</p>
        </div>
      )}
    </div>
  );
}